// TaskMetaBar.jsx - Compact task metadata bar
import React, { useState } from 'react';
import {
  Box,
  Chip,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  useTheme,
  alpha,
  Popover,
  Button,
} from '@mui/material';
import {
  Circle as TodoIcon,
  Schedule as InProgressIcon,
  RateReview as InReviewIcon,
  Check as DoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isToday, isTomorrow, isPast } from 'date-fns';

// Import stores
import useProjectStore from '../../../stores/projectStore';
import useAuthStore from '../../../stores/authStore';

// Status configuration
const STATUS_OPTIONS = [
  { value: 'to_do', label: 'To Do', icon: TodoIcon, color: 'default' },
  { value: 'in_progress', label: 'In Progress', icon: InProgressIcon, color: 'primary' },
  { value: 'in_review', label: 'In Review', icon: InReviewIcon, color: 'secondary' },
  { value: 'done', label: 'Done', icon: DoneIcon, color: 'success' },
];

// Priority configuration
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'success.main' },
  { value: 'medium', label: 'Medium', color: 'info.main' },
  { value: 'high', label: 'High', color: 'warning.main' },
  { value: 'urgent', label: 'Urgent', color: 'error.main' },
];

/**
 * TaskMetaBar - Displays and allows editing of task metadata in a compact bar
 */
const TaskMetaBar = ({
  status,
  priority,
  assignedTo,
  dueDate,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
  onDueDateChange,
  isMobile = false,
}) => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const { currentProject } = useProjectStore();
  
  // Menu anchors
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [priorityAnchor, setPriorityAnchor] = useState(null);
  const [assigneeAnchor, setAssigneeAnchor] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);
  
  // Get status config
  const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const StatusIcon = statusConfig.icon;
  
  // Get priority config
  const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[1];
  
  // Format due date
  const formatDueDate = (date) => {
    if (!date) return 'No due date';
    
    const dateObj = new Date(date);
    if (isToday(dateObj)) return 'Today';
    if (isTomorrow(dateObj)) return 'Tomorrow';
    if (isPast(dateObj)) return `Overdue (${format(dateObj, 'MMM d')})`;
    return format(dateObj, 'MMM d');
  };
  
  // Get assignee display
  const getAssigneeDisplay = () => {
    if (!assignedTo) return 'Unassigned';
    
    // If assignedTo is an object (populated)
    if (typeof assignedTo === 'object' && assignedTo._id) {
      const firstName = assignedTo.firstName || '';
      const lastName = assignedTo.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || 'Unknown User';
    }
    
    // If it's the current user
    if (assignedTo === user?._id) {
      return 'Me';
    }
    
    // Find user in project members
    const member = currentProject?.team?.find(m => m.user?._id === assignedTo);
    if (member?.user) {
      const firstName = member.user.firstName || '';
      const lastName = member.user.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || 'Unknown User';
    }
    
    return 'Assigned';
  };
  
  // Chip styles
  const getChipStyles = (isActive = false) => ({
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid',
    borderColor: 'transparent',
    '&:hover': {
      borderColor: theme.palette.divider,
      bgcolor: alpha(theme.palette.action.hover, 0.04),
    },
    ...(isActive && {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.primary.main,
    }),
  });
  
  return (
    <Box sx={{ 
      display: 'flex', 
      gap: isMobile ? 0.5 : 1,
      flexWrap: 'wrap',
      alignItems: 'center',
    }}>
      {/* Status */}
      <Chip
        icon={<StatusIcon sx={{ fontSize: 16 }} />}
        label={statusConfig.label}
        size="small"
        color={statusConfig.color}
        onClick={(e) => setStatusAnchor(e.currentTarget)}
        sx={{
          ...getChipStyles(Boolean(statusAnchor)),
          // Ensure proper contrast for primary color
          ...(statusConfig.color === 'primary' && {
            color: theme.palette.primary.contrastText,
            '& .MuiChip-icon': {
              color: theme.palette.primary.contrastText,
            },
          }),
           // Ensure proper contrast for primary color
          ...(statusConfig.color === 'primary' && {
            color: theme.palette.primary.contrastText,
            '& .MuiChip-label': {
              color: theme.palette.primary.contrastText,
            },
          }),
        }}
      />
      
      {/* Assignee */}
      <Chip
        icon={assignedTo ? (
          <Avatar sx={{ 
            width: 20, 
            height: 20, 
            fontSize: 10,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}>
            {getAssigneeDisplay()[0]}
          </Avatar>
        ) : (
          <PersonIcon sx={{ fontSize: 16 }} />
        )}
        label={getAssigneeDisplay()}
        size="small"
        variant="outlined"
        onClick={(e) => setAssigneeAnchor(e.currentTarget)}
        sx={getChipStyles(Boolean(assigneeAnchor))}
      />
      
      {/* Priority */}
      <Chip
        icon={<FlagIcon sx={{ fontSize: 16, color: priorityConfig.color }} />}
        label={isMobile ? `P${priority[0].toUpperCase()}` : priorityConfig.label}
        size="small"
        variant="outlined"
        onClick={(e) => setPriorityAnchor(e.currentTarget)}
        sx={{
          ...getChipStyles(Boolean(priorityAnchor)),
          '& .MuiChip-label': {
            color: priorityConfig.color,
          },
        }}
      />
      
      {/* Due Date */}
      <Chip
        icon={<CalendarIcon sx={{ 
          fontSize: 16,
          color: dueDate && isPast(new Date(dueDate)) ? 'error.main' : 'inherit',
        }} />}
        label={formatDueDate(dueDate)}
        size="small"
        variant="outlined"
        onClick={(e) => setDateAnchor(e.currentTarget)}
        sx={{
          ...getChipStyles(Boolean(dateAnchor)),
          ...(dueDate && isPast(new Date(dueDate)) && {
            borderColor: 'error.main',
            color: 'error.main',
          }),
        }}
      />
      
      {/* Status Menu */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {STATUS_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <MenuItem
              key={option.value}
              selected={status === option.value}
              onClick={() => {
                onStatusChange(option.value);
                setStatusAnchor(null);
              }}
              sx={{ gap: 1.5 }}
            >
              <Icon sx={{ fontSize: 18 }} />
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
      
      {/* Priority Menu */}
      <Menu
        anchorEl={priorityAnchor}
        open={Boolean(priorityAnchor)}
        onClose={() => setPriorityAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {PRIORITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={priority === option.value}
            onClick={() => {
              onPriorityChange(option.value);
              setPriorityAnchor(null);
            }}
            sx={{ gap: 1.5 }}
          >
            <FlagIcon sx={{ fontSize: 18, color: option.color }} />
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      
      {/* Assignee Menu */}
      <Menu
        anchorEl={assigneeAnchor}
        open={Boolean(assigneeAnchor)}
        onClose={() => setAssigneeAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={() => {
            onAssigneeChange(null);
            setAssigneeAnchor(null);
          }}
        >
          <PersonIcon sx={{ mr: 1.5, fontSize: 18 }} />
          Unassigned
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            onAssigneeChange(user._id);
            setAssigneeAnchor(null);
          }}
        >
          <Avatar sx={{ 
            width: 20, 
            height: 20, 
            mr: 1.5, 
            fontSize: 10,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}>
            {user.firstName?.[0]}
          </Avatar>
          Me
        </MenuItem>
        
        {currentProject?.team?.map((member) => {
          if (!member?.user) return null;
          return (
            <MenuItem
              key={member.user._id}
              onClick={() => {
                onAssigneeChange(member.user._id);
                setAssigneeAnchor(null);
              }}
            >
              <Avatar sx={{ 
                width: 20, 
                height: 20, 
                mr: 1.5, 
                fontSize: 10,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              }}>
                {member.user.firstName?.[0]}
              </Avatar>
              {member.user.firstName || ''} {member.user.lastName || ''}
            </MenuItem>
          );
        })}
      </Menu>
      
      {/* Date Picker Popover */}
      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Due Date"
              value={dueDate}
              onChange={(newValue) => {
                onDueDateChange(newValue);
                setDateAnchor(null);
              }}
              renderInput={({ inputRef, inputProps, InputProps }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input ref={inputRef} {...inputProps} style={{ display: 'none' }} />
                  {InputProps?.endAdornment}
                </Box>
              )}
            />
          </LocalizationProvider>
          
          {/* Quick date options */}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                onDueDateChange(new Date());
                setDateAnchor(null);
              }}
            >
              Today
            </Button>
            <Button
              size="small"
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                onDueDateChange(tomorrow);
                setDateAnchor(null);
              }}
            >
              Tomorrow
            </Button>
            <Button
              size="small"
              onClick={() => {
                const nextWeek = new Date();
                nextWeek.setDate(nextWeek.getDate() + 7);
                onDueDateChange(nextWeek);
                setDateAnchor(null);
              }}
            >
              Next Week
            </Button>
            {dueDate && (
              <Button
                size="small"
                color="error"
                onClick={() => {
                  onDueDateChange(null);
                  setDateAnchor(null);
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default TaskMetaBar;